// network/auth.jsx
import { jwtDecode } from 'jwt-decode';
import API_CONFIG from './api_config';
import useAuthStore from '../store/authstore';

export const registerUser = async (
  email,
  name,
  password,
  phoneNumber,
  isAdmin
) => {
  try {
    const response = await fetch(`${API_CONFIG.BACKEND_URL}/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password, name, phoneNumber, isAdmin }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Register user failed');
    }

    const responseData = await response.json();
    return responseData.data;
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};
/**
 * Handles user login
 * @param {string} email - User's email
 * @param {string} password - User's password
 * @returns {Promise<Object>} - Response from the server
 */
export const login = async (email, password) => {
  try {
    const response = await fetch(`${API_CONFIG.BACKEND_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Login failed');
    }

    const responseData = await response.json();
    const data = responseData.data;

    // Store tokens or session data as needed
    if (data.tokens && data.tokens.idToken) {
      localStorage.setItem('authToken', data.tokens.idToken);
      useAuthStore.getState().loggedin(jwtDecode(data.tokens.idToken));
    }

    return data;
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

/**
 * Handles user logout
 * @returns {Promise<Object>} - Response from the server
 */
export const logout = async () => {
  try {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_CONFIG.BACKEND_URL}/auth/logout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    // Clear local storage, store regardless of response
    localStorage.removeItem('authToken');
    useAuthStore.getState().loggedout(jwtDecode());

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Logout failed');
    }

    return await response.json();
  } catch (error) {
    console.error('Logout error:', error);
    throw error;
  }
};

/**
 * Fetches user data
 * @returns {Promise<Object>} - User data from the server
 */
export const getUsers = async () => {
  try {
    const idToken = localStorage.getItem('authToken');

    const response = await fetch(`${API_CONFIG.BACKEND_URL}/users`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to fetch users');
    }

    return await response.json();
  } catch (error) {
    console.error('Get users error:', error);
    throw error;
  }
};

/**
 * Checks if user is authenticated
 * @returns {boolean} - True if user has a valid token
 */
export const isAuthenticated = () => {
  const idToken = localStorage.getItem('authToken');
  if (!idToken) return false;

  try {
    // Decode and validate token
    const decoded = jwtDecode(idToken);
    const now = Date.now() / 1000; // Current time in seconds

    // Check expiration (exp is in seconds)
    if (decoded.exp < now) {
      console.warn('Token expired');
      localStorage.removeItem('idToken'); // Clean up
      return false;
    }

    // Verify token structure (basic checks)
    if (!decoded.sub || !decoded.iss.includes('cognito-idp')) {
      console.error('Invalid token structure');
      return false;
    }

    return true;
  } catch (error) {
    console.error('Token validation failed:', error);
    localStorage.removeItem('idToken'); // Clean up invalid tokens
    return false;
  }
};

/**
 * Gets the auth token
 * DO NOT USE THIS METHOD UNLESS YOU CANNOT USE THE DATA IN THE USEAUTH STORE
 * @returns {string|null} - The auth token or null if not found
 */
export const getAuthToken = () => {
  return localStorage.getItem('authToken');
};

// export const getCurrentUser = () => {
//   const token = getAuthToken();
//   return token ? jwtDecode(token) : null;
// };
